import {
  caseDetailDiff,
  setCaseDetailDiffRunner,
  type CaseDetailDiffContext,
  type CaseDetailDiffResult,
  type CaseDetailDiffRunner
} from '@codametrix/ui-common';
import { getWebpackPublicPath } from './asset-url';

type WorkerRequest = {
  id: number;
  publicPath?: string;
  phase1: CMxAPI.Case;
  phase2: CMxAPI.Case;
  context?: CaseDetailDiffContext;
};

type WorkerResponse = {
  id: number;
  result?: CaseDetailDiffResult;
  error?: string;
};

function abortError(signal: AbortSignal): DOMException {
  return signal.reason instanceof DOMException
    ? signal.reason
    : new DOMException('The operation was aborted.', 'AbortError');
}

export function createWorkerCaseDetailDiffRunner(): CaseDetailDiffRunner {
  if (typeof Worker === 'undefined') {
    return caseDetailDiff;
  }

  let worker: Worker;
  try {
    worker = new Worker(
      new URL('../workers/case-detail-diff.worker.ts', import.meta.url),
      { type: 'module' }
    );
  } catch {
    return caseDetailDiff;
  }

  let latestRequestId = 0;
  const pending = new Map<
    number,
    {
      resolve: (value: CaseDetailDiffResult) => void;
      reject: (reason: unknown) => void;
      cleanup: () => void;
    }
  >();

  worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
    const { id, result, error } = event.data;
    const entry = pending.get(id);
    if (!entry) {
      return;
    }

    pending.delete(id);
    entry.cleanup();

    if (id !== latestRequestId) {
      entry.resolve({ differs: false, changes: [] });
      return;
    }

    if (error) {
      entry.reject(new Error(error));
      return;
    }

    entry.resolve(result ?? { differs: false, changes: [] });
  };

  worker.onerror = () => {
    for (const [, entry] of pending) {
      entry.cleanup();
      entry.reject(new Error('Case detail diff worker failed'));
    }
    pending.clear();
  };

  return (phase1, phase2, context, signal) => {
    if (signal?.aborted) {
      return Promise.reject(abortError(signal));
    }

    const requestId = ++latestRequestId;

    return new Promise<CaseDetailDiffResult>((resolve, reject) => {
      const cleanup = () => {
        signal?.removeEventListener('abort', onAbort);
      };

      const onAbort = () => {
        if (!pending.has(requestId)) {
          return;
        }
        pending.delete(requestId);
        cleanup();
        reject(abortError(signal!));
      };

      pending.set(requestId, { resolve, reject, cleanup });
      signal?.addEventListener('abort', onAbort, { once: true });

      worker.postMessage({
        id: requestId,
        publicPath: getWebpackPublicPath(),
        phase1,
        phase2,
        context
      } satisfies WorkerRequest);
    });
  };
}

export function registerCaseDetailDiffWorkerRunner(): void {
  setCaseDetailDiffRunner(createWorkerCaseDetailDiffRunner());
}
